import { useState, useMemo } from 'react'
import { usePayments, useCreatePayment } from '@/features/payments/hooks/usePayments'
import { PaymentForm } from '@/features/payments/components/PaymentForm'
import { PaymentDetailModal } from '@/features/payments/components/PaymentDetailModal'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { DataTable } from '@/components/data-table/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Plus } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'

export function PaymentsListPage() {
  const { data: payments, isLoading } = usePayments()
  const createPayment = useCreatePayment()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<any>(null)

  const handleCreate = async (data: any) => {
    await createPayment.mutateAsync(data)
    setShowCreateModal(false)
  }

  // Enrich payments with sortable client_name and customer_name fields
  const paymentsWithSortFields = useMemo(() => {
    if (!payments) return []
    return payments.map(p => ({
      ...p,
      client_name: p.client?.name || '',
      customer_name: p.customer?.name || (p.customer_id === null ? 'zzz_deleted' : '') // Sort deleted last
    }))
  }, [payments])

  const columns = [
    { key: 'payment_date', header: 'Payment Date', render: (p: any) => formatDate(p.payment_date) },
    { key: 'client_name', header: 'Client', render: (p: any) => p.client?.name || '—' },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (p: any) => p.customer?.name || (p.customer_id === null ? 'Deleted customer' : '—')
    },
    { key: 'amount', header: 'Amount', render: (p: any) => formatCurrency(p.amount) },
    { key: 'payment_method', header: 'Method', render: (p: any) => <Badge>{p.payment_method}</Badge> },
    {
      key: 'collection_fee_owed',
      header: 'Fee Owed',
      render: (p: any) => p.is_pre_contract ? '—' : formatCurrency(p.collection_fee_owed)
    },
    {
      key: 'applied_to_baseline',
      header: 'Type',
      render: (p: any) => (
        <div className="flex gap-2">
          {p.is_pre_contract && (
            <Badge variant="default">Pre-contract</Badge>
          )}
          <Badge variant={p.applied_to_baseline ? 'info' : 'warning'}>
            {p.applied_to_baseline ? 'Baseline' : 'New Past-Due'}
          </Badge>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
          <p className="text-sm text-gray-600">View and manage all payment records</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Record Payment
        </Button>
      </div>

      <Card>
        <DataTable
          data={paymentsWithSortFields}
          columns={columns}
          onRowClick={(payment) => setSelectedPayment(payment)}
          loading={isLoading}
          emptyMessage="No payments recorded yet."
        />
      </Card>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Record Payment"
        size="lg"
      >
        <PaymentForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
          loading={createPayment.isPending}
        />
      </Modal>

      {selectedPayment && (
        <PaymentDetailModal
          payment={selectedPayment}
          isOpen={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </div>
  )
}
